import { useCallback, useEffect, useState } from "react";

/**
 * Hell/Dunkel für Kontor, Pflege und Team — ein Speicherschlüssel, damit ein
 * Nutzer mehrerer Apps nicht mehrmals umschalten muss. Werte für Kontor und
 * Pflege in styles/theme.css, Team bringt seine Farben in vgt.css mit.
 */
export type Farbthema = "light" | "dark";

const STORAGE_KEY = "vimaana_theme";
const THEME_EVENT = "vimaana:theme-change";

export function readTheme(): Farbthema {
  try {
    return localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function applyTheme(theme: Farbthema, persist = true) {
  document.documentElement.setAttribute("data-theme", theme);
  if (persist) {
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Das Design funktioniert auch ohne verfügbaren Browser-Speicher.
    }
  }
  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  themeColor?.setAttribute("content", theme === "dark" ? "#0b0b0c" : "#eceeec");
}

export function bootTheme() {
  applyTheme(readTheme(), false);
}

export function useTheme() {
  const [theme, setThemeState] = useState<Farbthema>(readTheme);

  useEffect(() => {
    const sync = (event: Event) => {
      const next = (event as CustomEvent<{ theme?: Farbthema }>).detail?.theme;
      if (next === "light" || next === "dark") setThemeState(next);
    };
    window.addEventListener(THEME_EVENT, sync);
    return () => window.removeEventListener(THEME_EVENT, sync);
  }, []);

  const setTheme = useCallback((next: Farbthema) => {
    applyTheme(next);
    setThemeState(next);
    window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { theme: next } }));
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === "dark" ? "light" : "dark");
  }, [setTheme, theme]);

  return { theme, setTheme, toggleTheme };
}
